import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Domain to language mapping
export const getDomainLanguage = (hostname: string): string => {
  if (hostname.includes('auditai.no')) return 'no';
  if (hostname.includes('auditai.dk')) return 'dk';
  if (hostname.includes('auditai.fi')) return 'fi';
  if (hostname.includes('auditai.se')) return 'sv';
  if (hostname.includes('auditai.tech')) return 'sv';
  return 'sv'; // Default fallback
};

// Get initial language based on domain and user preference
const getInitialLanguage = (): string => {
  // Check localStorage first for user preference
  const savedLanguage = localStorage.getItem('preferred-language');
  if (savedLanguage && ['sv', 'en', 'no', 'dk', 'fi'].includes(savedLanguage)) {
    return savedLanguage;
  }
  
  // Fall back to domain-based detection
  return getDomainLanguage(window.location.hostname);
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: {
      'no': ['en', 'sv'],
      'dk': ['en', 'sv'],
      'fi': ['en', 'sv'],
      'default': ['sv']
    },
    lng: getInitialLanguage(),
    
    supportedLngs: ['sv', 'en', 'no', 'dk', 'fi'],
    ns: ['common'],
    defaultNS: 'common',
    fallbackNS: 'common',
    
    debug: import.meta.env.DEV,
    
    keySeparator: '.',
    returnEmptyString: false,
    
    interpolation: {
      escapeValue: false,
      prefix: '{{',
      suffix: '}}',
    },
    
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'preferred-language',
    },

    resources: {
      sv: {
        common: undefined, // Will be loaded dynamically
      },
      en: {
        common: undefined,
      },
      no: {
        common: undefined,
      },
      dk: {
        common: undefined,
      },
      fi: {
        common: undefined,
      },
    },

    // Development helper to catch missing translations
    missingKeyHandler: (lng, ns, key, fallbackValue) => {
      if (import.meta.env.DEV) {
        console.warn(`Missing translation key: ${key} for language: ${lng}`);
      }
    },
  });

// Load translation files dynamically
const loadNamespace = async (lng: string, ns: string) => {
  try {
    // Use static version to prevent cache issues
    const response = await fetch(`/locales/${lng}/${ns}.json?v=1.0.2`);
    const translations = await response.json();
    i18n.addResourceBundle(lng, ns, translations, true, true); // Force overwrite existing
  } catch (error) {
    console.warn(`Failed to load translations for ${lng}/${ns}:`, error);
    // Fallback to Swedish if loading fails
    if (lng !== 'sv') {
      try {
        const fallbackResponse = await fetch(`/locales/sv/${ns}.json?v=1.0.2`);
        const fallbackTranslations = await fallbackResponse.json();
        i18n.addResourceBundle(lng, ns, fallbackTranslations, true, true);
      } catch (fallbackError) {
        console.error(`Failed to load fallback translations:`, fallbackError);
      }
    }
  }
};

// Initialize with common namespace
const initializeTranslations = async () => {
  const currentLng = i18n.language;
  await loadNamespace(currentLng, 'common');
  
  // Also load Swedish as fallback if not already current language
  if (currentLng !== 'sv') {
    await loadNamespace('sv', 'common');
  }
};

// Initialize translations
initializeTranslations();

// Load common namespace on language change
i18n.on('languageChanged', async (lng) => {
  await loadNamespace(lng, 'common');
  if (lng !== 'sv') {
    await loadNamespace('sv', 'common');
  }
});

// Helper function to change language and save preference
export const changeLanguage = (lng: string) => {
  localStorage.setItem('preferred-language', lng);
  i18n.changeLanguage(lng);
};

// Helper function to get available languages
export const getAvailableLanguages = () => [
  { code: 'sv', name: 'Svenska' },
  { code: 'en', name: 'English' },
  { code: 'no', name: 'Norsk' },
  { code: 'dk', name: 'Dansk' },
  { code: 'fi', name: 'Suomi' },
];

export default i18n;